import { type ReactElement, useEffect, useRef, useState } from "react"

import { Outlet, useLocation } from "react-router-dom"

import { ContentSidebar } from "@/components/layouts/content-sidebar"
import {
  isDesktopSplitContentSidebarLayout,
  shouldAutoCollapseLeftRailOnSplit,
} from "@/components/layouts/content-sidebar/content-sidebar-layout-utils"
import { SidebarProvider } from "@/components/ui/sidebar"
import { Cookies } from "@/constants/cookies"
import { useRoutes } from "@/contexts/use-routes"
import { AIExplorerSidebar } from "@/features/ai-explorer/components/ai-explorer-sidebar"
import { AIParserSidebar } from "@/features/ai-parser/components/ai-parser-sidebar"
import { ParserJobToastListener } from "@/features/ai-parser/components/parser-job-toast-listener"
import { AITranslatorSidebar } from "@/features/ai-translator/components/ai-translator-sidebar"
import { useCloseArtifactPanelOnRouteChange } from "@/features/artifacts/hooks/use-close-artifact-panel-on-route-change"
import { FactCheckBackgroundListener } from "@/features/chat/components/fact-check-background-listener"
import { CitationPreviewProvider } from "@/features/chat/contexts/citation-preview-context"
import { DashSidebar } from "@/features/dash/components/dash-sidebar"
import { OnboardingAwareWhatsNewDialog } from "@/features/onboarding/components/onboarding-aware-whats-new-dialog"
import { SlideSenseSidebar } from "@/features/slide-sense/components/SlideSenseSidebar"
import { TenderSidebar } from "@/features/tender/components/tender-sidebar"
import { TrainingSidebar } from "@/features/training/components/training-sidebar"
import { VideoInsightSidebar } from "@/features/video-insight/components/video-insight-sidebar"
import { WorkflowEditorSidebar } from "@/features/workflow-editor/components/workflow-editor-sidebar"
import { useViewportBelow } from "@/hooks/use-viewport-below"
import { cn } from "@/lib/utils"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"

import { AppSidebar } from "./app-sidebar"
import { getDynamicChatRoute } from "./dynamic-chat-routes"
import { Header } from "./header"

const COMPACT_LAYOUT_BREAKPOINT = 1024

const sidebarComponents: Record<string, ReactElement> = {
  free: <AIExplorerSidebar />,
  translate: <AITranslatorSidebar />,
  training: <TrainingSidebar />,
  "video-insight": <VideoInsightSidebar />,
  "ai-parser": <AIParserSidebar />,
  projects: <AIExplorerSidebar />,
  "workflow-editor": <WorkflowEditorSidebar />,
  tender: <TenderSidebar />,
  dash: <DashSidebar />,
  "slide-sense": <SlideSenseSidebar />,
}

function getInitialSidebarOpenState(): boolean {
  if (typeof document === "undefined") {
    return true
  }

  const sidebarCookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${Cookies.sidebarState.key}=`))

  if (!sidebarCookie) {
    return true
  }

  return sidebarCookie.split("=")[1] === "true"
}

export function AppLayout() {
  const location = useLocation()
  const { routes } = useRoutes()
  const isContentSidebarOpen = useContentSidebarStore((state) => state.isOpen)
  const isContentSidebarFocused = useContentSidebarStore((state) => state.isFocused)
  const forceClosePanel = useContentSidebarStore((state) => state.forceClosePanel)
  const isCompactLayout = useViewportBelow({ breakpoint: COMPACT_LAYOUT_BREAKPOINT })
  const path = location.pathname.split("/")[1]
  const pathSegments = location.pathname.split("/").filter(Boolean)
  const isDocumentsRoute = path === "documents" || location.pathname.includes("/documents/")
  const isProjectsRoute = path === "projects" || location.pathname.includes("/projects/")
  const isWorkflowLanding = location.pathname === "/flows"
  const [sidebarOpen, setSidebarOpen] = useState(getInitialSidebarOpenState)
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window === "undefined" ? Number.POSITIVE_INFINITY : window.innerWidth
  )
  const isDesktopSplitContentSidebar = isDesktopSplitContentSidebarLayout({
    hasPanel: isContentSidebarOpen,
    isFocused: isContentSidebarFocused,
    isCompactLayout,
  })
  const wasDocumentsRouteRef = useRef(isDocumentsRoute)
  const wasDesktopSplitContentSidebarRef = useRef(isDesktopSplitContentSidebar)

  useEffect(() => {
    const leftDocumentsWithContentSidebarOpen =
      wasDocumentsRouteRef.current && !isDocumentsRoute && isContentSidebarOpen

    if (leftDocumentsWithContentSidebarOpen) {
      setSidebarOpen(false)
    }

    wasDocumentsRouteRef.current = isDocumentsRoute
  }, [isContentSidebarOpen, isDocumentsRoute])

  useEffect(() => {
    const onResize = () => {
      setViewportWidth(window.innerWidth)
    }

    window.addEventListener("resize", onResize)
    onResize()

    return () => {
      window.removeEventListener("resize", onResize)
    }
  }, [])

  useEffect(() => {
    const enteredDesktopSplitContentSidebar =
      !wasDesktopSplitContentSidebarRef.current && isDesktopSplitContentSidebar

    if (
      enteredDesktopSplitContentSidebar &&
      shouldAutoCollapseLeftRailOnSplit({
        isDesktopSplitContentSidebar,
        viewportWidth,
      })
    ) {
      setSidebarOpen(false)
    }

    wasDesktopSplitContentSidebarRef.current = isDesktopSplitContentSidebar
  }, [isDesktopSplitContentSidebar, viewportWidth])

  const isFlowsRoute = path === "flows"
  useEffect(() => {
    if ((isFlowsRoute || isProjectsRoute) && isContentSidebarOpen) {
      forceClosePanel()
    }
  }, [location.pathname, isFlowsRoute, isProjectsRoute, forceClosePanel])

  useCloseArtifactPanelOnRouteChange()

  // Find the current route from the config
  // No longer need to handle empty path since / redirects to a named route
  const currentRoute = routes.find((route) => path && route.path === `/${path}`)

  // Check if we're in the workflow editor (has ID or is /flows/new)
  const isWorkflowEditor =
    path === "flows" && (pathSegments.length > 1 || location.pathname === "/flows/new")

  // Choose the appropriate sidebar component
  const dynamicChatRoute = path ? getDynamicChatRoute(path) : undefined
  let SidebarComponent: ReactElement | null = <AppSidebar />
  let sidebarKey = "default"

  if (isWorkflowLanding || isWorkflowEditor) {
    // Both landing page and editor use the workflow sidebar
    SidebarComponent = sidebarComponents["workflow-editor"]
    sidebarKey = "workflow-editor"
  } else if (path === "training") {
    // Special case for training path
    SidebarComponent = sidebarComponents.training
    sidebarKey = "training"
  } else if (isProjectsRoute || isDocumentsRoute) {
    // Projects routes use AI Explorer sidebar
    SidebarComponent = sidebarComponents.projects
    sidebarKey = "projects"
  } else if (dynamicChatRoute) {
    // Handle dynamic routes like /chat/:sessionId (chat maps to free/Explorer)
    const component = dynamicChatRoute.component
    SidebarComponent = sidebarComponents[component]
    sidebarKey = component
  } else if (currentRoute) {
    // Named routes like /explorer, /custom-variant, /translate
    const component = currentRoute.component
    SidebarComponent = sidebarComponents[component]
    sidebarKey = component
  }

  // Rail-less modules — those whose `component` has no entry in
  // `sidebarComponents` (admin-dashboard, studio_ai, ai-portfolio,
  // segment-analysis, local-ai-ambassador, …) — resolve
  // `SidebarComponent` to undefined and render no left rail, and therefore no
  // logo→home affordance (the logo lives only in AppSidebar). Tell the Header
  // so it can surface a logo→home in that case, guaranteeing every module has a
  // way home without forcing a near-empty AppSidebar onto full-bleed modules.
  const hasLeftRail = Boolean(SidebarComponent)

  return (
    <SidebarProvider
      className="relative min-h-full w-full"
      includeContentSidebarOpenInMobileDetection={false}
      open={sidebarOpen}
      onOpenChange={setSidebarOpen}
    >
      <CitationPreviewProvider>
        <a
          className="focus:bg-primary focus:text-primary-foreground sr-only fixed top-2 left-2 z-[200] rounded-md px-4 py-2 text-sm font-medium focus:not-sr-only focus:outline-none"
          href="#page-content"
        >
          Skip to content
        </a>
        <section className="flex h-svh w-full overflow-hidden" data-testid="main-layout">
          <ParserJobToastListener />
          <FactCheckBackgroundListener />
          {SidebarComponent}
          <section
            data-testid="main-content"
            className={cn("flex h-full w-full flex-col overflow-hidden", {
              "overflow-hidden": currentRoute?.component === "ai-parser",
            })}
          >
            <Header hasLeftRail={hasLeftRail} />
            <div
              className="flex min-h-0 flex-1 flex-col overflow-hidden"
              data-testid="page-content"
              id="page-content"
            >
              <ContentSidebar>
                <div key={sidebarKey} className="module-enter min-h-0 flex-1 overflow-y-auto">
                  <Outlet />
                </div>
              </ContentSidebar>
            </div>
          </section>
        </section>
        <OnboardingAwareWhatsNewDialog />
      </CitationPreviewProvider>
    </SidebarProvider>
  )
}
